import { createFileRoute } from '@tanstack/react-router';
import React, { useState, useRef } from 'react';
import { useStore } from '../../store';
import { ProjectCard } from '../../components/projects';
import { send } from '../../ws';
import { ConfirmDialog } from '../../components/ConfirmDialog';
import { ListControls } from '../../components/ListControls';
import { useListControls } from '../../hooks/useListControls';
import { useFocusTrap } from '../../utils/focusTrap';
import { useAppContext } from '../../contexts/AppContext';
import styles from '../../components/ProjectsView.module.css';
import dialogStyles from '../../components/CreateProjectDialog.module.css';
import { correlationId } from "@interlucent/ids";
import { logger } from '../../log';

const log = logger('projects');

export const Route = createFileRoute('/projects/')({
  component: ProjectsListComponent,
});

function ProjectsListComponent() {
  const { errorMessage, setErrorMessage } = useAppContext();
  const isConnected = useStore(s => s.connectionState === 'connected');
  const projects = useStore(s => s.projects);
  const applications = useStore(s => s.applications);
  const versions = useStore(s => s.versions);
  const [showCreateProject, setShowCreateProject] = useState(false);
  const [projectName, setProjectName] = useState('');
  const createProjectModalRef = useRef<HTMLDivElement>(null);

  useFocusTrap(createProjectModalRef, showCreateProject);

  const [confirmDialog, setConfirmDialog] = useState<{
    isOpen: boolean;
    title: string;
    message: string;
    onConfirm: () => void;
    requireConfirmation?: boolean;
    confirmationPhrase?: string;
  }>({
    isOpen: false,
    title: '',
    message: '',
    onConfirm: () => {},
  });

  const handleCreateProject = () => {
    if (!projectName.trim()) {
      alert('Project name is required');
      return;
    }

    const slugHint = projectName.toLowerCase().replace(/[^a-z0-9]+/g, '-');
    const corr = correlationId();
    log.info('sending create project', { name: projectName, slugHint, corr });

    setErrorMessage('');
    send('project.create', { name: projectName.trim(), slugHint }, corr);
    setShowCreateProject(false);
    setProjectName('');
  };

  const projectList = React.useMemo(() => Object.values(projects), [projects]);

  // Per-project haystacks include each project's own fields plus its applications
  // and the versions of those applications, so a search at the project level can
  // narrow projects by app name, version description, executable, env vars, etc.
  // Versions for an app are only present in the store after that app's snapshot
  // chunk has been loaded — unloaded versions simply won't match.
  const projectHaystacks = React.useMemo(() => {
    const appsByProject = new Map<string, typeof applications[string][]>();
    for (const app of Object.values(applications)) {
      const list = appsByProject.get(app.projectId) ?? [];
      list.push(app);
      appsByProject.set(app.projectId, list);
    }

    const map = new Map<string, string>();
    for (const p of projectList) {
      const parts: string[] = [p.name, p.description ?? '', p.projectSlug ?? '', p.id];
      const projectApps = appsByProject.get(p.id) ?? [];
      for (const app of projectApps) {
        parts.push(app.name, app.description ?? '', app.appSlug ?? '', app.id);
        const appVersions = Object.values(versions).filter(v => v.appId === app.id);
        for (const v of appVersions) {
          parts.push(
            v.id ?? '',
            v.executablePath ?? '',
            v.cmdArgs ?? '',
            v.environmentVariables ?? '',
            v.notes ?? '',
            v.description ?? '',
          );
        }
      }
      map.set(p.id, parts.filter(Boolean).join(' '));
    }
    return map;
  }, [projectList, applications, versions]);

  const { search, setSearch, sort, setSort, visible: visibleProjects } = useListControls(projectList, {
    getSearchHaystack: (p) => projectHaystacks.get(p.id) ?? p.name,
    getName: (p) => p.name,
    getCreatedAt: (p) => p.createdAt,
    getUpdatedAt: (p) => p.updatedAt,
  });

  const handleDeleteProject = (projectId: string, projectName: string) => {
    setConfirmDialog({
      isOpen: true,
      title: 'Delete Project',
      message: `This will permanently delete "${projectName}" and all its applications and versions.`,
      requireConfirmation: true,
      confirmationPhrase: projectName,
      onConfirm: () => {
        const corr = correlationId();
        log.info('sending delete project', { projectId, corr });
        setErrorMessage('');
        send('project.delete', { projectId }, corr);
        setConfirmDialog(prev => ({ ...prev, isOpen: false }));
      },
    });
  };

  return (
    <div className={styles.container}>
      <h1 className={styles.pageTitle}>Projects</h1>
      <div className={styles.tabToolbar}>
        <ListControls
          search={search}
          onSearchChange={setSearch}
          sort={sort}
          onSortChange={setSort}
          searchPlaceholder="Search projects…"
        />
        <button
          onClick={() => setShowCreateProject(true)}
          disabled={!isConnected}
          className={styles.createButton}
        >
          Create Project
        </button>
      </div>

      {errorMessage && (
        <div className={styles.errorMessage}>
          <span className={styles.errorText}>{errorMessage}</span>
          <button onClick={() => setErrorMessage('')} className={styles.errorClose}>
            ×
          </button>
        </div>
      )}

      {projectList.length === 0 ? (
        <div className={styles.emptyState}>
          No projects yet. Create one to get started.
        </div>
      ) : visibleProjects.length === 0 ? (
        <div className={styles.emptyState}>
          No projects match your search.
        </div>
      ) : (
        <div className={styles.projectList}>
          {visibleProjects.map(p => {
            const projectAppCount = Object.values(applications).filter(app => app.projectId === p.id).length;
            return (
              <ProjectCard
                key={p.id}
                project={p}
                applicationCount={projectAppCount}
                onClick={() => {}}
                onDelete={() => handleDeleteProject(p.id, p.name)}
                disabled={!isConnected}
              />
            );
          })}
        </div>
      )}

      {showCreateProject && (
        <div
          className={dialogStyles.modalOverlay}
          onClick={() => {
            setShowCreateProject(false);
            setProjectName('');
          }}
          role="dialog"
          aria-modal="true"
          aria-labelledby="create-project-title"
        >
          <div ref={createProjectModalRef} className={dialogStyles.modal} onClick={(e) => e.stopPropagation()}>
            <h2 id="create-project-title" className={dialogStyles.modalTitle}>Create New Project</h2>
            <form
              onSubmit={(e) => {
                e.preventDefault();
                handleCreateProject();
              }}
            >
              <div className={dialogStyles.formGroup}>
                <label htmlFor="project-name-input" className={dialogStyles.label}>
                  Project Name *
                </label>
                <input
                  id="project-name-input"
                  type="text"
                  value={projectName}
                  onChange={(e) => setProjectName(e.target.value)}
                  placeholder="My Awesome Project"
                  autoFocus
                  className={dialogStyles.input}
                  required
                />
              </div>
              <div className={dialogStyles.buttonGroup}>
                <button
                  type="button"
                  onClick={() => {
                    setShowCreateProject(false);
                    setProjectName('');
                  }}
                  className={`${dialogStyles.button} ${dialogStyles.buttonSecondary}`}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={!projectName.trim()}
                  className={dialogStyles.button}
                >
                  Create Project
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      <ConfirmDialog
        isOpen={confirmDialog.isOpen}
        title={confirmDialog.title}
        message={confirmDialog.message}
        onConfirm={confirmDialog.onConfirm}
        onCancel={() => setConfirmDialog(prev => ({ ...prev, isOpen: false }))}
        confirmText="Delete"
        requireConfirmation={confirmDialog.requireConfirmation}
        confirmationPhrase={confirmDialog.confirmationPhrase}
      />
    </div>
  );
}
